import React, { useEffect, useState } from "react";
import { Sparkles, Target, TrendingUp, MapPin, Globe, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";

const fmt = (n, prefix = "₹") =>
  n == null ? "—" : `${prefix}${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;

function MiniBar({ data, format = (v) => v }) {
  if (!data || data.length === 0) return <p className="text-[10px] text-slate-400">No data.</p>;
  const max = Math.max(...data.map((d) => d.median || 0));
  return (
    <div className="space-y-1">
      {data.slice(0, 6).map((d) => {
        const pct = max > 0 ? Math.min(100, Math.round(((d.median || 0) / max) * 100)) : 0;
        return (
          <div key={d.key} className="flex items-center gap-2 text-[10px]">
            <span className="w-16 truncate text-slate-600">{d.key}</span>
            <div className="flex-1 bg-slate-100 rounded h-2 relative">
              <div className="h-2 rounded bg-blue-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono w-20 text-right text-slate-700">{format(d.median)}</span>
            <span className="text-slate-400 w-10 text-right">×{d.samples}</span>
            {d.win_rate_pct != null && (
              <span className={`w-10 text-right font-mono ${
                d.win_rate_pct >= 60 ? "text-emerald-600" :
                d.win_rate_pct >= 40 ? "text-amber-600" : "text-rose-600"
              }`}>
                {d.win_rate_pct.toFixed(0)}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PricingSuggestPanel({ lineId, onApplyRate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    if (!lineId) return;
    setLoading(true); setErr(null);
    try {
      const d = await v2Api.pricing.suggestForLine(lineId);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail ?? "Could not load price suggestion");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [lineId]);

  if (loading && !data) {
    return <p className="text-xs text-slate-500 py-2">Computing price band…</p>;
  }
  if (err) {
    return <p className="text-xs text-rose-600 py-2">{err}</p>;
  }
  if (!data) return null;

  const { band, recommended_price_inr, recommended_floor_inr, recommended_ceiling_inr,
          expected_win_pct, rationale, history, competitor_observations,
          state_distribution, portal_distribution,
          catalog_base_price_inr, catalog_list_price_inr, sku_part_number, sku_name, category } = data;

  const adoptRate = () => {
    if (recommended_price_inr == null) return;
    onApplyRate?.(recommended_price_inr);
    toast.success(`Applied ₹${recommended_price_inr.toLocaleString("en-IN")} to the line rate.`);
  };

  return (
    <div className="bg-blue-50/40 border-y border-blue-100 px-4 py-3 space-y-3 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Pricing intelligence
          </p>
          <p className="font-medium text-slate-800">
            {sku_part_number ? <><span className="font-mono text-blue-700">{sku_part_number}</span> · {sku_name}</> : "(no matched SKU)"}
            {category && <span className="text-slate-500"> · {category}</span>}
          </p>
          <p className="text-slate-600 mt-0.5">{rationale}</p>
        </div>
        {recommended_price_inr != null && (
          <div className="shrink-0 text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Recommended</p>
            <p className="text-base font-semibold text-emerald-700">
              {fmt(recommended_price_inr)}
            </p>
            {expected_win_pct != null && (
              <p className="text-[10px] text-slate-500">
                Beats <span className="font-mono">{expected_win_pct.toFixed(0)}%</span> of comparable bids
              </p>
            )}
            <Button size="sm" className="mt-1 h-7 text-xs" onClick={adoptRate}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Apply to rate
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ["Floor", band.floor],
          ["P25", band.p25],
          ["Median", band.median],
          ["P75", band.p75],
          ["Ceiling", band.ceiling],
        ].map(([label, v]) => (
          <div key={label} className="bg-white border rounded p-1.5 text-center">
            <p className="text-[9px] uppercase text-slate-400 font-bold tracking-wider">{label}</p>
            <p className="font-mono text-[11px] text-slate-800">{fmt(v)}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <div className="bg-white border rounded p-1.5">
          <p className="uppercase tracking-wider text-slate-400 font-bold">Catalog base</p>
          <p className="font-mono text-slate-700">{fmt(catalog_base_price_inr)}</p>
        </div>
        <div className="bg-white border rounded p-1.5">
          <p className="uppercase tracking-wider text-slate-400 font-bold">Catalog list</p>
          <p className="font-mono text-slate-700">{fmt(catalog_list_price_inr)}</p>
        </div>
        <div className="bg-white border rounded p-1.5">
          <p className="uppercase tracking-wider text-slate-400 font-bold">Sample size</p>
          <p className="font-mono text-slate-700">{band.sample_size}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> By state
          </p>
          <MiniBar data={state_distribution} format={fmt} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
            <Globe className="w-3 h-3" /> By portal
          </p>
          <MiniBar data={portal_distribution} format={fmt} />
        </div>
      </div>

      {(history.length + competitor_observations.length) > 0 && (
        <details className="text-[10px]">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-semibold">
            <TrendingUp className="w-3 h-3 inline mr-1" />
            See raw evidence ({history.length} our bids · {competitor_observations.length} competitor obs)
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto bg-white border rounded">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Source</th>
                  <th className="px-2 py-1 text-left">Project / Competitor</th>
                  <th className="px-2 py-1 text-left">State</th>
                  <th className="px-2 py-1 text-left">Portal</th>
                  <th className="px-2 py-1 text-right">Price</th>
                  <th className="px-2 py-1 text-left">Outcome</th>
                  <th className="px-2 py-1 text-left">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...history, ...competitor_observations].map((h, i) => (
                  <tr key={i}>
                    <td className="px-2 py-0.5 text-slate-500">{h.source.replace("_", " ")}</td>
                    <td className="px-2 py-0.5 text-slate-700 truncate max-w-[180px]">{h.project}</td>
                    <td className="px-2 py-0.5">{h.client_state || "—"}</td>
                    <td className="px-2 py-0.5">{h.portal || "—"}</td>
                    <td className="px-2 py-0.5 text-right font-mono">{fmt(h.price_inr)}</td>
                    <td className="px-2 py-0.5">
                      <span className={
                        h.outcome === "won" ? "text-emerald-600" :
                        h.outcome === "lost" ? "text-rose-600" : "text-slate-500"
                      }>
                        {h.outcome}
                      </span>
                    </td>
                    <td className="px-2 py-0.5 font-mono text-slate-500">{h.date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
